import {
  setPropertyData,
} from "@/redux/slices";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import {
  useGetBomDashboardStat,
  useGetIncidentDashboardCount,
  useGetPropertiesByCommunity,
  useGetPropertiesByCommunityAndUser,
  useGetTransactionHistory,
} from "@/services";
import { Building2, CalendarDays, CheckCircle2, TriangleAlert } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toNumber } from "./consts";
import moment from "moment";
import { formatAmount } from "@/lib/utils";

const useHeaderCard = () => {
  const navigate = useNavigate();
  const [propertyKey, setPropertyKey] = useState<number>(Math.random() * 3);

  const dispatch = useDispatch();

  const {
    communityData: communityDetail,
    currentFinanceYear,
    loggedInUserData,
    propertyData,
  } = useSelector((state: RootState) => state.currentData);

  const { data: communityStats, isLoading: isCommunityStatLoading } =
    useGetBomDashboardStat({
      communityId: communityDetail?.communityId,
      queryParams: {
        enabled: !!communityDetail?.communityId,
      },
    });

  const { data: propertyList, isLoading: isLoadingProperties } =
    useGetPropertiesByCommunityAndUser({
      communityId: communityDetail?.communityId as string,
      queryParams: {
        enabled: !!communityDetail?.communityId,
      },
    });

  const { data: communityPropertyList } = useGetPropertiesByCommunity({
    communityId: communityDetail?.communityId as string,
    queryParams: {
      enabled: !!communityDetail?.communityId,
    },
  });

  const { data: incidentDashboardCount, isLoading: isIncidentLoading } =
    useGetIncidentDashboardCount({
      communityId: communityDetail?.communityId as string,
      userId: loggedInUserData?.userId as string,
      queryParams: {
        enabled: !!(communityDetail?.communityId && loggedInUserData?.userId),
      },
    });

  const { data: pendingBillsData, refetch: refetchPendingBillsData } = useGetTransactionHistory({
    communityId: communityDetail?.communityId,
    data: {
      filters: [
        {
          field: "communityId",
          value: communityDetail?.communityId,
          operator: "EQUAL",
        },
        {
          field: "userId",
          value: loggedInUserData?.userId,
          operator: "EQUAL",
        },
        {
          field: "propertyId",
          value: propertyData?.propertyId,
          operator: "EQUAL",
        },
        {
          field: "status",
          value: "PENDING",
          operator: "CONTAINS",
        },
      ],
      pageNumber: 0,
      pageSize: 1000,
      sort: [
        {
          field: "createdAt",
          direction: "DESC",
        },
      ],
    },
    queryParams: {
      enabled: !!(
        communityDetail?.communityId &&
        loggedInUserData?.userId &&
        propertyData?.propertyId
      ),
    },
  });

  const { data: completedBillsData } = useGetTransactionHistory({
    communityId: communityDetail?.communityId,
    data: {
      filters: [
        {
          field: "communityId",
          value: communityDetail?.communityId,
          operator: "EQUAL",
        },
        {
          field: "userId",
          value: loggedInUserData?.userId,
          operator: "EQUAL",
        },
        {
          field: "propertyId",
          value: propertyData?.propertyId,
          operator: "EQUAL",
        },
        {
          field: "status",
          value: "COMPLETED",
          operator: "CONTAINS",
        },
      ],
      pageNumber: 0,
      pageSize: 1000,
      sort: [
        {
          field: "updatedAt",
          direction: "DESC",
        },
      ],
    },
    queryParams: {
      enabled: !!(
        communityDetail?.communityId &&
        loggedInUserData?.userId &&
        propertyData?.propertyId
      ),
    },
  });

  useEffect(() => {
    if (
      communityDetail?.communityId &&
      loggedInUserData?.userId &&
      propertyData?.propertyId
    ) {
      refetchPendingBillsData();
    }
  }, [
    communityDetail?.communityId,
    loggedInUserData?.userId,
    propertyData?.propertyId,
  ]);

  useEffect(() => {
    if (propertyList) {
      const isSelectedProperty = propertyList?.data?.some(
        (data: any) =>
          data?.propertyId?.toString() == propertyData?.propertyId?.toString()
      );

      if (!isSelectedProperty) {
        dispatch(setPropertyData(propertyList?.data?.[0]));
        setPropertyKey(Math.random() * 2);
      }
    }
  }, [propertyList, propertyData?.propertyId]);

  const onPropertySubCardClick = (link: any) => () => {
    link
      ? navigate(link, {
          state: {
            from: link?.includes(routes.transactionHistory) ? "dashboard" : "",
          },
        })
      : "";
  };

  const onPropertyChange = (id: string) => {
    const data = propertyList?.data?.find((data: any) => data?.propertyId?.toString() == id);
    dispatch(setPropertyData(data));
    setPropertyKey(Math.random() * 3)
  };

  const reportedIncident = toNumber(incidentDashboardCount?.data?.reportedCount);
  const responsibleIncident = toNumber(incidentDashboardCount?.data?.responsibleCount);
  const openIncidentCount = reportedIncident + responsibleIncident;
  const pendingBillCount = useMemo(
    () => toNumber(pendingBillsData?.total ?? pendingBillsData?.data?.length),
    [pendingBillsData]
  );
  const pendingBalance = toNumber(pendingBillsData?.data?.[0]?.balance);
  const propertiesCount = communityPropertyList?.data?.length || 0;
  const lastPaidBill =
    completedBillsData?.data?.find(
      (bill: any) => Number(bill?.credit || bill?.writeOff) > 0
    ) ?? completedBillsData?.data?.[0];
  const lastPaymentDate = useMemo(() => lastPaidBill?.updatedAt
    ? moment(lastPaidBill.updatedAt).format("MM/DD/YYYY")
    : "", [lastPaidBill])
  const lastPaymentAmount = lastPaidBill?.credit || lastPaidBill?.writeOff;
  const communityLocation = useMemo(() => [
    propertyData?.address?.city,
    propertyData?.address?.state,
  ].filter(Boolean).join(", "), [propertyData]);
  const isSnapshotLoading = isLoadingProperties || isIncidentLoading || isCommunityStatLoading;

  const snapshotCards = [
    {
      title: "Properties Active",
      value: propertiesCount,
      subValue: "Total properties in the community",
      className: "bg-blue-50 border-blue-100",
      icon: Building2,
      iconClassName: "bg-blue-100 text-primary",
      textClassName: "text-primary",
    },
    {
      title: "Coming Soon",
      comingSoon: true,
      className: "bg-green-50 border-green-100",
      iconClassName: "bg-blue-100 text-primary",
    },
    {
      title: "Last Payment",
      value: lastPaymentDate || "No Records Found",
      subValue: lastPaymentDate && lastPaymentAmount != null
        ? formatAmount(lastPaymentAmount)
        : "",
      icon: CalendarDays,
      className: "bg-violet-50 border-violet-100",
      iconClassName: "bg-violet-100 text-violet-600",
      textClassName: "text-violet-600",
    },
  ];

  const alertCards = [
    {
      title: openIncidentCount
        ? `${openIncidentCount} Open Incidents`
        : "No Open Incidents",
      subTitle: openIncidentCount
        ? `${reportedIncident} Reported, ${responsibleIncident} Responsible`
        : "Great! Everything is clear.",
      icon: openIncidentCount ? TriangleAlert : CheckCircle2,
      iconClassName: openIncidentCount
        ? "bg-red-600 text-white"
        : "bg-green-600 text-white",
      onClick: openIncidentCount
        ? onPropertySubCardClick(`/${routes.owner}/${routes.incidentMgmt}`)
        : undefined,
    },
    {
      title: "Outstanding Amount",
      subTitle: pendingBillCount
        ? `Balance: ${formatAmount(pendingBalance)}`
        : "You're all caught up!",
      icon: pendingBillCount ? TriangleAlert : CheckCircle2,
      iconClassName: pendingBillCount
        ? "bg-amber-500 text-white"
        : "bg-green-600 text-white",
      onClick: pendingBillCount
        ? onPropertySubCardClick(`/${routes.owner}/${routes.transactionHistory}`)
        : undefined,
    },
  ];

  return {
    communityDetail,
    loggedInUserData,
    propertyData,
    navigate,
    onPropertySubCardClick,
    isLoadingProperties,
    propertyList,
    onPropertyChange,
    currentFinanceYear,
    isIncidentLoading,
    communityStats,
    isCommunityStatLoading,
    incidentDashboardCount,
    communityLocation,
    isSnapshotLoading,
    snapshotCards,
    alertCards,
    propertyKey
  };
};

export default useHeaderCard;
